import type { ModalProps } from '@chakra-ui/react';
import { Box, Button, Flex, Modal, ModalContent, Text } from '@chakra-ui/react';
import { pathDict } from '@ui/config/paths';
import { isFeatureEnabled } from '@ui/hooks/useIsFeatureEnabled';
import { useTranslations } from '@ui/i18n';
import Link from '@ui/overrides/next/link';
import { formatPercentage, formatTokenAmount } from '@ui/utils/format';
import { useOwnedDepositCoinAmount, useStrategyApy } from '@ui/utils/strategy';

import Features, { features } from '../Features';
import { TokenIcon } from '../TokenIcon';
import { SAFE_BOTTOM_SPACE } from '.';
import { NavigationButton, NavigationCard } from './NavigationCard';
import StakeCoinNavigationCard from './StakeCoinNavigationCard';

export const NavigationModal = (props: Omit<ModalProps, 'children'>) => {
  const t = useTranslations();
  const strategyApyRet = useStrategyApy();
  const ownedDepositAmountRet = useOwnedDepositCoinAmount();

  return (
    <Modal size="full" scrollBehavior="inside" {...props}>
      <ModalContent
        bgColor="bg-gauze"
        display="flex"
        justifyContent="flex-end"
        alignItems="center"
        pb={`${SAFE_BOTTOM_SPACE}px`}
        containerProps={{
          zIndex: 'banner',
        }}
      >
        {isFeatureEnabled(features.Atom) ? (
          <Flex w="full" px={4} flexDir="column" gap={3}>
            <Features.strategyVault>
              <NavigationCard
                icon={<TokenIcon token="cro" w="32px" h="32px" />}
                title={
                  <Flex justify="space-between">
                    <Text textTransform="uppercase">
                      {t('Liquidity strategy')}
                    </Text>
                    <Text textAlign="right" textTransform="uppercase">
                      {strategyApyRet.data && strategyApyRet.data > 0
                        ? formatPercentage(strategyApyRet.data)
                        : '--'}{' '}
                      APY
                    </Text>
                  </Flex>
                }
                subtitle={
                  <Text>
                    {t('Your value')}:{' '}
                    {formatTokenAmount(ownedDepositAmountRet ?? 0, 2)} CRO
                  </Text>
                }
              >
                <Link href={pathDict.cro.strategy.withdraw} passHref>
                  <NavigationButton
                    as="a"
                    w="30%"
                    textTransform="uppercase"
                    onClick={props.onClose}
                  >
                    {t('Withdraw')}
                  </NavigationButton>
                </Link>
                <Link href={pathDict.cro.strategy.deposit} passHref>
                  <NavigationButton
                    as="a"
                    w="30%"
                    isPrimary
                    textTransform="uppercase"
                    onClick={props.onClose}
                  >
                    {t('Deposit')}
                  </NavigationButton>
                </Link>
              </NavigationCard>
            </Features.strategyVault>
            <StakeCoinNavigationCard coinMode="atom" onClose={props.onClose} />
            <StakeCoinNavigationCard coinMode="cro" onClose={props.onClose} />
          </Flex>
        ) : (
          <Flex flexDir="column" gap={4}>
            <Features.Garden>
              <Box textAlign="center" onClick={props.onClose}>
                <Link href={pathDict.cro.useLiquid}>
                  <Button as="a" variant="appMenu">
                    Use LCRO
                  </Button>
                </Link>
              </Box>
            </Features.Garden>
            <Box textAlign="center" onClick={props.onClose}>
              <Link href={pathDict.cro.unstake}>
                <Button as="a" variant="appMenu">
                  Unstake CRO
                </Button>
              </Link>
            </Box>
            <Box textAlign="center" onClick={props.onClose}>
              <Link href={pathDict.cro.stake}>
                <Button as="a" variant="appMenu">
                  Stake CRO
                </Button>
              </Link>
            </Box>
          </Flex>
        )}
      </ModalContent>
    </Modal>
  );
};
